/* eslint-disable max-len */
import { useIsMutating } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParam, useTimeout } from 'react-use';
import styled from 'styled-components';
import { deleteSearchParam, uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { RangeResult, UserLiteData } from '../../../api/ApiTypes';
import { TaskData } from '../../../api/endpoints/tasks';
import { Icon } from '../../../concepts/resources/Icon';
import { Button, Colors, Input, Spinner } from '../../../ui';
import { LoadingGuard } from '../../../ui/LoadingGuard';
import { PageButtonStrip } from '../../../ui/PageButtonStrip';
import { FadeOut } from '../../../ui/Transitions';
import { useResolve } from '../../Container';
import { AccountHeaderBlock } from '../../account/AccountHeaderBlock';
import { PlanStateHeaderBlock } from '../../account/PlanStateHeaderBlock';
import { DataTable } from '../../common/DataTable';
import { Page2 } from '../../common/Page2';
import { WorkspaceModel } from '../Models';
import { EmptyBlock } from '../elements/EmptyBlock';
import { Flexer } from '../elements/LayoutElements';
import { TaskBulkActionMenu } from './TaskBulkActionMenu';
import { TaskCreateButton } from './TaskCreateButton';
import { useTaskQuery } from './TaskQueries';
import { useTaskTableModel } from './TaskTable';
import { TaskTableTheme } from './TaskTableTheme';
import { TasksUserFilter } from './TasksUserFilter';

const TasksLoading = () => {
    const patching = useIsMutating(['tasks.patch']);
    const deleting = useIsMutating(['tasks.delete']);
    const mutating = patching || deleting;

    const [key, setKey] = useState(null as string);

    useEffect(() => {
        if (!!mutating) {
            setKey(uuid());
        }
    }, [mutating]);

    return <>
        {!!mutating &&
            <Flexer>
                <Spinner size={1.5} />
            </Flexer>
        }
        {!mutating && !!key &&
            <FadeOut key={key} after="4000">
                <Flexer flex="row" center gap=".375" color={Colors.positive}>
                    <Icon src="deqo://icons/hero/check-circle" size={1.25} /> Saved
                </Flexer>
            </FadeOut>
        }
    </>;
};

const RowHighlighter = styled.div<{ row?: string }>`
    tr[data-row="${p => p.row}"] {
        animation: highlightAnim 4s linear forwards;
    }

    @keyframes highlightAnim {
        0% {
            background: ${Colors.yellow400};
        }
        50% {
            background: ${Colors.yellow400};
        }
        100% {
            background: white;
        }
    }
`;

const useRowToHighlight = (data: RangeResult<TaskData>) => {
    const taskId = useSearchParam('highlight');

    const [clearParam] = useTimeout(1000);
    if (clearParam()) {
        deleteSearchParam('highlight');
    }

    if (taskId?.startsWith('TA')) {
        return data?.items?.findIndex(x => x.id === taskId)?.toString();
    }

    return null;
};

const Frame = styled.div`
    min-width: 75rem;
    border-radius: .5rem;
    padding: 1rem;
    border: solid 1px ${Colors.borderLight};
    background: white;
`;

const TasksMainView = () => {

    const { id: workspaceId } = useResolve(WorkspaceModel);
    const [selectedUsers, setSelectedUsers] = useState([] as UserLiteData[]);

    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('');
    const [startAt, setStartAt] = useState(0);
    const userFilter = useMemo(() => selectedUsers.map(x => x.id), [selectedUsers]);
    const [statusFilter, setStatusFilter] = useState('');
    const { data, isInitialLoading } = useTaskQuery({ workspaceId, search, userFilter, statusFilter, sort, startAt });
    const rowToHighlight = useRowToHighlight(data);

    const hasItems = !!data?.items?.length;
    const hasFilters = !!search || !!userFilter.length || !!statusFilter;

    const onClearFilters = () => {
        setSearch('');
        setSelectedUsers([]);
        setStatusFilter('');
    };

    // Reset startAt on search
    useEffect(() => {
        setStartAt(0);
    }, [search]);

    const { table, selection, clearSelection } = useTaskTableModel({
        items: data?.items ?? [],
        onSortChange: setSort,
    });

    return <LoadingGuard loading={isInitialLoading}>
        <Frame>
            <Flexer flex="row" gap="1" align="center" justify="space-between">
                <Flexer flex="row" gap="1" align="center">
                    <TaskCreateButton />
                    <TaskBulkActionMenu selection={selection} onAction={clearSelection} />
                    <Flexer width="20">
                        <Input placeholder="Search" value={search} onChange={setSearch} />
                    </Flexer>
                    <TasksUserFilter {...{ workspaceId, selected: selectedUsers, onSelect: setSelectedUsers }} />
                    {hasFilters && <Button variant="text" onAction={onClearFilters}>Clear Filters</Button>}
                </Flexer>
                <Flexer flex="row" gap="1" align="center" shrink>
                    <TasksLoading />
                    <PageButtonStrip data={data} onPageChange={setStartAt} />
                </Flexer>
            </Flexer>
            <Flexer height="1" />
            <RowHighlighter row={rowToHighlight}>
                {hasItems && <DataTable theme={TaskTableTheme} table={table} selectFromFirstCellOnly />}
            </RowHighlighter>
            {!hasItems && !hasFilters && <>
                <EmptyBlock minHeight="24"
                    title="Action Items help you keep track of meeting outcomes."
                    subtitle="After your next meeting, assign a sticky note to a team member, and it will show up here."
                />
            </>}
        </Frame>
    </LoadingGuard>;
};

export const TasksView = () =>
    <Page2.Scroll>
        <Page2.Header>
            <Page2.Title>
                Actions
            </Page2.Title>
            <Page2.HeaderRight>
                <PlanStateHeaderBlock />
                <AccountHeaderBlock />
            </Page2.HeaderRight>
        </Page2.Header>
        <Page2.Content>
            <Flexer maxWidth="128">
                <TasksMainView />
            </Flexer>
        </Page2.Content>
    </Page2.Scroll>;

